<template>
  <s-input-wrapper v-bind="$props" :class="['input-richtext', {focused: isFocused}]">
    <div class="richtext-container">
      <div class="richtext-toolbar" role="toolbar" aria-label="Text formatting">
        <button
          type="button"
          class="richtext-toolbar-button"
          title="Bold"
          aria-label="Bold"
          :disabled="disabled || readonly"
          @mousedown.prevent
          @click="format('bold')"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          class="richtext-toolbar-button"
          title="Italic"
          aria-label="Italic"
          :disabled="disabled || readonly"
          @mousedown.prevent
          @click="format('italic')"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          class="richtext-toolbar-button"
          title="Title"
          aria-label="Title"
          :disabled="disabled || readonly"
          @mousedown.prevent
          @click="format('title')"
        >
          T
        </button>
        <button
          type="button"
          class="richtext-toolbar-button"
          title="Bullet list"
          aria-label="Bullet list"
          :disabled="disabled || readonly"
          @mousedown.prevent
          @click="format('bullet-list')"
        >
          <span aria-hidden="true">&#8226; &#8801;</span>
        </button>
        <button
          type="button"
          class="richtext-toolbar-button"
          title="Clear formatting"
          aria-label="Clear formatting"
          :disabled="disabled || readonly"
          @mousedown.prevent
          @click="clearFormatting"
        >
          <span aria-hidden="true">Tx</span>
        </button>
      </div>
      <div
        :id="id"
        ref="editor"
        class="s-input-group-field richtext-editor"
        :class="{'richtext-editor-empty': !editorText}"
        :contenteditable="!disabled && !readonly"
        :data-placeholder="_placeholder"
        :aria-disabled="disabled"
        :aria-readonly="readonly"
        role="textbox"
        aria-multiline="true"
        @input="onInput"
        @change="$_onChange"
        @click="$_onClick"
        @keydown="$_onKeyPressed"
        @focus="onFocused"
        @blur="onBlured"
        @paste="handlePaste"
      />
    </div>
  </s-input-wrapper>
</template>

<script>
  import DOMPurify from 'dompurify';
  import {marked} from 'marked';
  import SInputWrapper from '@veasel/inputs/input-wrapper';
  import {
    syncValue,
    onChange,
    onClick,
    onBlur,
    onFocus,
    onKeyPressed,
    onPaste,
    id,
    name,
    readonly,
    placeholder,
    isDisabled,
    isRequired,
    pattern,
    maxLength,
    helperMessages,
  } from '@veasel/core/mixins';

  const SANITIZE_OPTIONS = {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'li', 'p', 'div', 'br'],
    ALLOWED_ATTR: [],
  };

  export default {
    name: 's-input-richtext',
    description: 'A rich text input that outputs Markdown or HTML.',
    components: {
      SInputWrapper,
    },
    mixins: [
      syncValue(String),
      onChange(),
      onClick(),
      onBlur(),
      onFocus(),
      onKeyPressed(),
      onPaste(),
      id,
      name,
      readonly,
      placeholder,
      isDisabled,
      isRequired,
      pattern,
      maxLength,
      helperMessages,
    ],
    props: {
      outputFormat: {
        description: 'Format emitted through v-model.',
        type: String,
        values: ['markdown', 'html'],
        validator: (value) => ['markdown', 'html'].includes(value),
        default: 'html',
      },
    },
    data() {
      return {
        editorText: '',
        isFocused: false,
        lastEmittedValue: undefined,
      };
    },
    watch: {
      modelValue: {
        immediate: true,
        handler() {
          if (this.modelValue === this.lastEmittedValue) return;
          if (this.$refs.editor) this.setEditorContent();
        },
      },
      outputFormat() {
        this.setEditorContent();
      },
    },
    mounted() {
      this.setEditorContent();
    },
    methods: {
      sanitizeHtml(html) {
        return DOMPurify.sanitize(html || '', SANITIZE_OPTIONS);
      },
      getEditorHtml(value = this.modelValue) {
        if (!value) return '';
        return this.outputFormat === 'html'
          ? this.sanitizeHtml(value)
          : this.sanitizeHtml(marked.parse(value, {async: false}));
      },
      setEditorContent() {
        if (!this.$refs.editor) return;
        const html = this.getEditorHtml();
        if (this.$refs.editor.innerHTML !== html) this.$refs.editor.innerHTML = html;
        this.editorText = this.$refs.editor.textContent || '';
      },
      escapeMarkdown(value) {
        return value.replace(/([\\`*_[\]])/g, '\\$1');
      },
      htmlToMarkdown(html) {
        const container = document.createElement('div');
        container.innerHTML = this.sanitizeHtml(html);

        const convert = (node) => {
          if (node.nodeType === Node.TEXT_NODE) return this.escapeMarkdown(node.textContent);
          if (node.nodeType !== Node.ELEMENT_NODE) return '';

          const content = Array.from(node.childNodes).map(convert).join('');
          switch (node.tagName.toLowerCase()) {
            case 'strong':
            case 'b':
              return `**${content}**`;
            case 'em':
            case 'i':
              return `*${content}*`;
            case 'h1':
              return `\n\n# ${content.trim()}\n\n`;
            case 'h2':
              return `\n\n## ${content.trim()}\n\n`;
            case 'h3':
              return `\n\n### ${content.trim()}\n\n`;
            case 'h4':
              return `\n\n#### ${content.trim()}\n\n`;
            case 'h5':
              return `\n\n##### ${content.trim()}\n\n`;
            case 'h6':
              return `\n\n###### ${content.trim()}\n\n`;
            case 'br':
              return '\n';
            case 'li':
              return `- ${content.trim()}\n`;
            case 'ul':
              return `\n${content}\n`;
            case 'p':
            case 'div':
              return `\n\n${content.trim()}\n\n`;
            default:
              return content;
          }
        };

        return Array.from(container.childNodes)
          .map(convert)
          .join('')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
      },
      escapeMarkdownText(value) {
        return (value || '')
          .replace(/\\/g, '\\\\')
          .replace(/`/g, '\\`')
          .replace(/\*/g, '\\*')
          .replace(/_/g, '\\_')
          .replace(/\[/g, '\\[')
          .replace(/\]/g, '\\]');
      },
      normalizeMarkdown(value) {
        return (value || '')
          .replace(/\u00a0/g, ' ')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
      },
      nodeToMarkdownInline(node) {
        if (node.nodeType === Node.TEXT_NODE) return this.escapeMarkdownText(node.textContent);
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const tagName = node.tagName.toLowerCase();
        const content = Array.from(node.childNodes)
          .map((child) => this.nodeToMarkdownInline(child))
          .join('');

        switch (tagName) {
          case 'strong':
          case 'b':
            return content.trim() ? `**${content}**` : '';
          case 'em':
          case 'i':
            return content.trim() ? `*${content}*` : '';
          case 'br':
            return '\n';
          case 'div':
          case 'p':
            return Array.from(node.childNodes)
              .map((child) => this.nodeToMarkdownInline(child))
              .join('')
              .trim();
          default:
            return content;
        }
      },
      listToMarkdown(node, ordered = false) {
        return Array.from(node.children)
          .filter((child) => child.tagName?.toLowerCase() === 'li')
          .map((child, index) => {
            const content = Array.from(child.childNodes)
              .map((childNode) => {
                if (
                  childNode.nodeType === Node.ELEMENT_NODE &&
                  ['ul', 'ol'].includes(childNode.tagName.toLowerCase())
                ) {
                  return `\n${this.listToMarkdown(childNode, childNode.tagName.toLowerCase() === 'ol')}`;
                }

                return this.nodeToMarkdownInline(childNode);
              })
              .join('')
              .trim();

            return `${ordered ? `${index + 1}.` : '-'} ${content}`;
          })
          .join('\n');
      },
      nodeToMarkdownBlock(node) {
        if (node.nodeType === Node.TEXT_NODE) return this.escapeMarkdownText(node.textContent).trim();
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const tagName = node.tagName.toLowerCase();
        const inlineContent = () =>
          Array.from(node.childNodes)
            .map((child) => this.nodeToMarkdownInline(child))
            .join('')
            .trim();

        switch (tagName) {
          case 'h1':
            return `# ${inlineContent()}`;
          case 'h2':
            return `## ${inlineContent()}`;
          case 'h3':
            return `### ${inlineContent()}`;
          case 'h4':
            return `#### ${inlineContent()}`;
          case 'h5':
            return `##### ${inlineContent()}`;
          case 'h6':
            return `###### ${inlineContent()}`;
          case 'ul':
            return this.listToMarkdown(node);
          case 'ol':
            return this.listToMarkdown(node, true);
          case 'br':
            return '';
          case 'div':
          case 'p':
            return inlineContent();
          default:
            return this.nodeToMarkdownInline(node).trim();
        }
      },
      htmlToNormalizedMarkdown(html) {
        const container = document.createElement('div');
        container.innerHTML = this.sanitizeHtml(html);

        return this.normalizeMarkdown(
          Array.from(container.childNodes)
            .map((node) => this.nodeToMarkdownBlock(node))
            .filter(Boolean)
            .join('\n\n')
        );
      },
      getOutput(html) {
        const cleanHtml = this.sanitizeHtml(html);
        return this.outputFormat === 'html' ? cleanHtml : this.htmlToNormalizedMarkdown(cleanHtml);
      },
      truncateEditor() {
        if (this.maxlength === -1) return;

        let remaining = this.maxlength;
        const textNodes = document.createTreeWalker(this.$refs.editor, NodeFilter.SHOW_TEXT);
        let node = textNodes.nextNode();
        while (node) {
          if (remaining <= 0) {
            node.textContent = '';
          } else if (node.textContent.length > remaining) {
            node.textContent = node.textContent.substring(0, remaining);
          }
          remaining -= node.textContent.length;
          node = textNodes.nextNode();
        }
      },
      onInput() {
        const editor = this.$refs.editor;
        this.truncateEditor();
        const cleanHtml = this.sanitizeHtml(editor.innerHTML);
        if (editor.innerHTML !== cleanHtml) editor.innerHTML = cleanHtml;
        this.editorText = editor.textContent || '';
        this.lastEmittedValue = this.getOutput(cleanHtml);
        this.$_value = this.lastEmittedValue;
      },
      getCurrentBlockTag() {
        const selection = window.getSelection?.();
        let node = selection?.anchorNode;

        if (!node || !this.$refs.editor.contains(node)) return '';
        if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;

        while (node && node !== this.$refs.editor) {
          const tagName = node.tagName?.toLowerCase();
          if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'li'].includes(tagName)) return tagName;
          node = node.parentElement;
        }

        return '';
      },
      insertBulletListFallback(previousHtml) {
        const editor = this.$refs.editor;
        const commandDidNothing = editor.innerHTML === previousHtml;
        const hasList = Boolean(editor.querySelector('ul, ol'));

        if (!commandDidNothing || hasList) return;

        const text = editor.textContent.trim();
        editor.innerHTML = `<ul><li>${text ? this.escapeHtml(text) : '<br>'}</li></ul>`;
      },
      escapeHtml(value) {
        return (value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      },
      clearFormatting() {
        const editor = this.$refs.editor;
        const lines = (editor.textContent || '')
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line !== '');

        editor.innerHTML = lines.length ? lines.map((line) => `<div>${this.escapeHtml(line)}</div>`).join('') : '';
        this.onInput();
      },
      format(type) {
        this.$refs.editor.focus();

        if (type === 'title') {
          document.execCommand('formatBlock', false, this.getCurrentBlockTag() === 'h2' ? 'div' : 'h2');
        } else if (type === 'bullet-list') {
          const previousHtml = this.$refs.editor.innerHTML;
          document.execCommand('insertUnorderedList', false, undefined);
          this.insertBulletListFallback(previousHtml);
        } else {
          const commands = {
            bold: ['bold'],
            italic: ['italic'],
          };
          const [command, value] = commands[type];
          document.execCommand(command, false, value);
        }

        this.onInput();
      },
      handlePaste(event) {
        this.$_onPaste(event);
        event.preventDefault();
        const text = event.clipboardData ? event.clipboardData.getData('text/plain') : '';
        document.execCommand('insertText', false, text);
        this.onInput();
      },
      onFocused() {
        this.isFocused = true;
        this.$_onFocus();
      },
      onBlured(event) {
        this.isFocused = false;
        this.$_onChange(event);
        this.$_onBlur();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@veasel/core';

  .richtext-container {
    position: relative;
    flex-grow: 1;
  }

  .richtext-toolbar {
    position: absolute;
    top: rem(4px);
    right: rem(4px);
    z-index: 1;
    display: flex;
    gap: rem(4px);
    padding: rem(2px);
    border: 1px solid var(--secondary-20);
    border-radius: $button-radius;
    background: var(--background-main);
    box-shadow: 0 rem(1px) rem(4px) rgba(0, 0, 0, 0.08);
  }

  .richtext-toolbar-button {
    @include caption;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: rem(30px);
    height: rem(28px);
    border: 1px solid var(--secondary-20);
    border-radius: $button-radius;
    background: var(--background-main);
    color: var(--secondary);
    cursor: pointer;
    line-height: 1;
    padding: 0 rem(6px);

    &:hover:not(:disabled),
    &:focus-visible {
      background: var(--secondary-20);
      color: var(--primary);
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  .richtext-editor {
    @include base;

    min-height: rem(52px);
    padding: rem(42px) rem(8px) rem(8px);
    outline: 0;
    white-space: pre-wrap;

    &.richtext-editor-empty::before {
      content: attr(data-placeholder);
      color: var(--secondary-50);
      pointer-events: none;
    }

    ::v-deep(h1),
    ::v-deep(h2),
    ::v-deep(h3),
    ::v-deep(h4),
    ::v-deep(h5),
    ::v-deep(h6),
    ::v-deep(p) {
      margin: 0 0 rem(8px);
    }

    ::v-deep(ul) {
      margin: 0 0 rem(8px);
      padding-left: rem(20px);
      list-style: disc outside;
    }

    ::v-deep(li) {
      display: list-item;
    }
  }

  .focused .richtext-toolbar {
    border-color: var(--primary-30);
  }
</style>
